import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import ReactSelect from 'react-select';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, CreditCard, PackageCheck, Search } from 'lucide-react';
import { getTransactions } from '../../lib/apiService';

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderRadius: '0.75rem',
    fontSize: '12px',
    borderColor: state.isFocused ? '#ee7923' : '#e2e8f0',
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 1px #ee7923' : 'none',
    '&:hover': { borderColor: '#cbd5e1' }
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center'
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px 6px'
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px 6px'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '0.75rem',
    zIndex: 9999,
    fontSize: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)'
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    padding: '6px 12px',
    backgroundColor: state.isSelected ? '#ee7923' : state.isFocused ? '#f1f5f9' : 'white',
    color: state.isSelected ? 'white' : '#1e293b'
  })
};

const TYPE_BADGE = {
  plan:  'bg-sky-50 text-sky-700 border border-sky-200',
  addon: 'bg-violet-50 text-violet-700 border border-violet-200',
  boost: 'bg-orange-50 text-orange-700 border border-orange-200',
};

const STATUS_BADGE = {
  success: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  pending: 'bg-amber-50 text-amber-700 border border-amber-300',
  failed:  'bg-red-50 text-red-700 border border-red-300',
  expired: 'bg-slate-100 text-slate-600 border border-slate-300',
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  </Card>
);

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTransactions({ type: filterType, search, month: filterMonth });
      setTransactions(res.transactions || []);
      setSummary(res.summary || {});
      setTotal(res.total || 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filterType, search, filterMonth]);

  useEffect(() => { 
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchTransactions]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Transactions | JoldiPabo Admin</title>
      </Helmet>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Transactions & Earnings
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">All revenue from subscriptions, addons, and boosts</p>
        </div>

        {/* Search & Filters on top right before refresh */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="relative w-56 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="h-[38px] pl-9 text-xs rounded-xl border-slate-200 bg-white shadow-none"
              placeholder="Search user, phone, ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="w-44">
            <ReactSelect
              styles={selectStyles}
              options={[
                { value: '', label: 'All Types' },
                { value: 'plan', label: 'Plan Subscriptions' },
                { value: 'addon', label: 'Addons' },
                { value: 'boost', label: 'Property Boosts' },
              ]}
              value={
                filterType
                  ? {
                      value: filterType,
                      label: filterType === 'plan' ? 'Plan Subscriptions' : filterType === 'addon' ? 'Addons' : 'Property Boosts'
                    }
                  : { value: '', label: 'All Types' }
              }
              onChange={opt => setFilterType(opt?.value || '')}
              isSearchable={false}
            />
          </div>

          <div className="w-48 relative">
            <input 
              type="month" 
              value={filterMonth} 
              onChange={e => setFilterMonth(e.target.value)}
              style={{ height: '38px', fontSize: '12px', paddingRight: '8px' }}
              className="w-full pl-3 rounded-xl border border-slate-200 bg-white text-slate-700 outline-none focus:border-[#ee7923] cursor-pointer"
              title="Filter by month"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={fmt(summary.total_revenue || 0)}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          icon={CreditCard}
          label="Plan Subscriptions"
          value={summary.plan_count || 0}
          color="bg-sky-100 text-sky-600"
        />
        <StatCard
          icon={PackageCheck}
          label="Addon Purchases"
          value={summary.addon_count || 0}
          color="bg-violet-100 text-violet-600"
        />
        <StatCard
          icon={TrendingUp}
          label="Property Boosts"
          value={summary.boost_count || 0}
          color="bg-orange-100 text-orange-600"
        />
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="border-b border-slate-200 px-6 py-4">
          <span className="text-sm font-medium text-slate-700">{total} transactions</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No transactions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Plan / Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Payment ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, idx) => (
                  <tr key={`${t.transaction_type}-${t.id}-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{t.user_name || '—'}</p>
                      <p className="text-xs text-slate-400">{t.user_phone || t.user_email || ''}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{t.plan_name || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{fmt(t.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TYPE_BADGE[t.transaction_type] || TYPE_BADGE.plan}`}>
                        {t.transaction_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[t.status] || STATUS_BADGE.pending}`}>
                        {t.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400 font-mono">{t.payment_id || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TransactionsPage;
